import React, { useEffect, useState } from "react";
import { Plus, Pin, Star, Trash2, Pencil, Search, StickyNote, Loader2, PanelRightOpen, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { smartSearch } from "../lib/search";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const COLORS = {
  amber: "bg-amber-100 border-amber-300 text-amber-950",
  blue: "bg-blue-100 border-blue-300 text-blue-950",
  green: "bg-emerald-100 border-emerald-300 text-emerald-950",
  pink: "bg-pink-100 border-pink-300 text-pink-950",
  purple: "bg-purple-100 border-purple-300 text-purple-950",
  orange: "bg-orange-100 border-orange-300 text-orange-950",
};
const COLOR_KEYS = Object.keys(COLORS);
const empty = { title: "", content: "", color: "amber", category: "", pinned: false, favorite: false };

export default function Notebook() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState(true);

  const load = () => { setLoading(true); api.get("/notebook").then((r) => setNotes(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); const h = () => load(); window.addEventListener("notes:changed", h); return () => window.removeEventListener("notes:changed", h); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (n) => { setEditing(n); setForm({ ...empty, ...n }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/notebook/${editing.id}`, form);
      else await api.post("/notebook", form);
      toast.success("Nota guardada"); setOpen(false); load(); window.dispatchEvent(new Event("notes:changed"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (n) => { await api.delete(`/notebook/${n.id}`); toast.success("Nota eliminada"); load(); window.dispatchEvent(new Event("notes:changed")); };
  const quickToggle = async (n, field) => { await api.put(`/notebook/${n.id}`, { ...n, [field]: !n[field] }); load(); window.dispatchEvent(new Event("notes:changed")); };

  const filtered = smartSearch(notes, q, ["title", "content", "category"]);

  return (
    <div className="flex gap-4">
      <div className="flex-1 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="note-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar notas..." className="pl-9 rounded-sm" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" className="rounded-sm lg:hidden" onClick={() => setTab(!tab)}><PanelRightOpen className="h-4 w-4" /></Button>
            <Button onClick={openCreate} className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs" data-testid="new-note-btn"><Plus className="h-4 w-4" /> Nueva nota</Button>
          </div>
        </div>

        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
          filtered.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-muted-foreground"><StickyNote className="h-10 w-10 mb-2" /><p className="text-sm">Sin notas todavía.</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="notes-grid">
              {filtered.map((n) => (
                <div key={n.id} className={`rounded-sm border p-4 flex flex-col min-h-[140px] ${COLORS[n.color] || COLORS.amber}`} data-testid="note-card">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-heading font-bold leading-tight flex-1">{n.title || "(sin título)"}</p>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => quickToggle(n, "pinned")} title="Fijar"><Pin className={`h-4 w-4 ${n.pinned ? "fill-current" : "opacity-40"}`} /></button>
                      <button onClick={() => quickToggle(n, "favorite")} title="Favorita"><Star className={`h-4 w-4 ${n.favorite ? "fill-current" : "opacity-40"}`} /></button>
                    </div>
                  </div>
                  <p className="text-sm mt-2 flex-1 whitespace-pre-wrap break-words">{n.content}</p>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-black/10">
                    {n.category ? <span className="text-[11px] font-bold uppercase tracking-wide opacity-70">{n.category}</span> : <span />}
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(n)} data-testid="edit-note" className="p-1 hover:opacity-70"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(n)} data-testid="delete-note" className="p-1 hover:opacity-70"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Retractable side tab */}
      {tab && (
        <aside className="hidden lg:block w-60 shrink-0">
          <div className="sticky top-24 border border-border rounded-sm bg-card">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
              <span className="text-xs font-bold uppercase tracking-widest">Acceso rápido</span>
              <button onClick={() => setTab(false)} data-testid="close-note-tab"><X className="h-4 w-4 text-muted-foreground" /></button>
            </div>
            <div className="p-3 space-y-3">
              <Button onClick={openCreate} size="sm" className="w-full rounded-sm gap-2"><Plus className="h-4 w-4" /> Nueva nota</Button>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Fijadas</p>
                {notes.filter((n) => n.pinned).slice(0, 4).map((n) => <button key={n.id} onClick={() => openEdit(n)} className="block w-full text-left text-sm py-1 truncate hover:text-primary">{n.title || "(sin título)"}</button>)}
                {notes.filter((n) => n.pinned).length === 0 && <p className="text-xs text-muted-foreground">Ninguna</p>}
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Favoritas</p>
                {notes.filter((n) => n.favorite).slice(0, 4).map((n) => <button key={n.id} onClick={() => openEdit(n)} className="block w-full text-left text-sm py-1 truncate hover:text-primary">{n.title || "(sin título)"}</button>)}
                {notes.filter((n) => n.favorite).length === 0 && <p className="text-xs text-muted-foreground">Ninguna</p>}
              </div>
            </div>
          </div>
        </aside>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="note-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar nota" : "Nueva nota"}</DialogTitle>
            <DialogDescription>Notas privadas, solo tú las ves.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Título" className="rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="note-title" />
            <Textarea placeholder="Escribe tu nota..." className="rounded-sm min-h-[120px]" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="note-content" />
            <Input placeholder="Categoría (opcional)" className="rounded-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="note-category" />
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest">Color</Label>
              <div className="flex gap-2 mt-2">
                {COLOR_KEYS.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, color: c })} data-testid={`note-color-${c}`}
                    className={`h-7 w-7 rounded-sm border-2 ${COLORS[c]} ${form.color === c ? "ring-2 ring-primary ring-offset-1" : ""}`} />
                ))}
              </div>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Fijar</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.favorite} onChange={(e) => setForm({ ...form, favorite: e.target.checked })} /> Favorita</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={save} data-testid="save-note-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
